import { useEffect, useState, useCallback } from 'react';

import { PageHeader, useWorkspace } from '../../components/layout/WorkspaceShell';
import Btn from '../../components/ui/Btn';
import { Card, Badge, Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from '../auth/SignInScreen';
import { supabase } from '../../lib/supabase';
import { useConfirm } from '../../lib/confirm';
import { useToast } from '../../lib/toast';
import { useIsMobile } from '../../lib/useBreakpoint';

// Convert "Reviewer" → "reviewer", "Office Manager" → "office_manager", etc.
function toSlug(label) {
  return (label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 40);
}

// Permission flags shown as toggle switches in the editor
const PERMISSIONS = [
  {
    key: 'is_staff',
    label: 'Staff member',
    hint: 'Can access the full workspace (not just the upload portal). Required for any other permission to take effect.',
    icon: 'shield',
  },
  {
    key: 'can_review',
    label: 'Review documents',
    hint: 'Approve, reject, and reassign uploads.',
    icon: 'check',
  },
  {
    key: 'can_invite',
    label: 'Invite members',
    hint: 'Send invites to new clients and staff.',
    icon: 'send',
  },
  {
    key: 'can_manage_templates',
    label: 'Manage templates',
    hint: 'Create, edit, and delete document templates and their role assignments.',
    icon: 'edit',
  },
];

export default function RolesPanel() {
  const { teamId, isOwner, role: myRole, refresh } = useWorkspace();
  const isMobile = useIsMobile();
  const confirm = useConfirm();
  const toast = useToast();
  const canEditRoles = isOwner; // Only owners can manage roles. Could relax later.

  const [roles, setRoles]         = useState([]);
  const [memberCounts, setMemberCounts] = useState({}); // role_id -> count
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [editingId, setEditingId] = useState(null); // 'new' or role.id

  const loadRoles = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, membersRes] = await Promise.allSettled([
        supabase
          .from('team_roles')
          .select('*')
          .eq('team_id', teamId)
          .order('is_staff', { ascending: false })
          .order('name'),
        supabase
          .from('team_members')
          .select('role_id')
          .eq('team_id', teamId),
      ]);

      if (rolesRes.status === 'rejected' || rolesRes.value?.error) {
        throw rolesRes.reason || rolesRes.value?.error || new Error('Could not load roles.');
      }

      const counts = {};
      const memberRows = membersRes.status === 'fulfilled' ? (membersRes.value?.data || []) : [];
      memberRows.forEach(m => {
        if (m.role_id) counts[m.role_id] = (counts[m.role_id] || 0) + 1;
      });

      setRoles(rolesRes.value.data || []);
      setMemberCounts(counts);
    } catch (err) {
      console.error('Roles load failed:', err);
      setError(err.message || 'Failed to load roles.');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const deleteRole = async (role) => {
    if (!canEditRoles) return;
    const memberCount = memberCounts[role.id] || 0;
    if (memberCount > 0) {
      toast.info({
        title: 'Role is in use',
        message: `This role is currently assigned to ${memberCount} member${memberCount === 1 ? '' : 's'}. Reassign them to a different role before deleting.`,
      });
      return;
    }
    const r = await confirm({
      title: 'Delete role',
      message: `Delete the "${role.name}" role? This can't be undone.`,
      confirmLabel: 'Delete role',
      confirmVariant: 'danger',
    });
    if (!r.confirmed) return;

    try {
      // Clean up template_role_assignments first to avoid FK conflicts
      await supabase
        .from('template_role_assignments')
        .delete()
        .eq('role_id', role.id);

      const { error: delErr } = await supabase
        .from('team_roles')
        .delete()
        .eq('id', role.id);
      if (delErr) throw delErr;

      setRoles(roles.filter(r => r.id !== role.id));
      refresh?.();
    } catch (err) {
      console.error('Delete role failed:', err);
      toast.error('Could not delete: ' + (err.message || 'unknown error'));
    }
  };

  if (loading && roles.length === 0) {
    return (
      <>
        <PageHeader title="Roles" subtitle="Loading…" />
        <Card style={{ padding: 48, textAlign: 'center' }}>
          <Spinner size={20} color="var(--accent)" />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Roles"
        subtitle={`${roles.length} role${roles.length === 1 ? '' : 's'} · Define who can do what in this workspace.`}
        actions={
          canEditRoles ? (
            <Btn variant="primary" size="md" icon={Icon.plus} onClick={() => setEditingId('new')}>
              New role
            </Btn>
          ) : null
        }
      />

      {error && <ErrorBanner message={error} style={{ marginBottom: 16 }} />}

      {!canEditRoles && (
        <Card style={{ padding: 14, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon.info size={16} stroke="var(--text-3)" />
          <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
            Only workspace owners can manage roles.
          </div>
        </Card>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: isMobile ? 10 : 14,
      }}>
        {roles.map(role => (
          <RoleCard
            key={role.id}
            role={role}
            memberCount={memberCounts[role.id] || 0}
            canEdit={canEditRoles}
            onEdit={() => setEditingId(role.id)}
            onDelete={() => deleteRole(role)}
          />
        ))}

        {canEditRoles && (
          <button
            type="button"
            onClick={() => setEditingId('new')}
            style={{
              border: '1.5px dashed var(--border-strong)',
              background: 'transparent',
              borderRadius: 'var(--r-lg)',
              padding: 18, minHeight: 170,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', cursor: 'pointer',
              fontFamily: 'inherit', color: 'var(--text-3)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent)';
              e.currentTarget.style.background = 'var(--accent-bg)';
              e.currentTarget.style.color = 'var(--accent-600)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-strong)';
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-3)';
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'var(--surface-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 10,
            }}>
              <Icon.plus size={18} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
              Create a role
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Office Manager, Reviewer, Auditor, etc.
            </div>
          </button>
        )}
      </div>

      {editingId && (
        <RoleEditModal
          mode={editingId === 'new' ? 'new' : 'edit'}
          role={editingId === 'new' ? null : roles.find(r => r.id === editingId)}
          allRoles={roles}
          teamId={teamId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); loadRoles(); refresh?.(); }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function RoleCard({ role, memberCount, canEdit, onEdit, onDelete }) {
  const isMobile = useIsMobile();
  const permissions = [
    role.is_staff && 'Staff',
    role.can_review && 'Review',
    role.can_invite && 'Invite',
    role.can_manage_templates && 'Templates',
  ].filter(Boolean);

  return (
    <Card style={{ padding: isMobile ? 14 : 18, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: role.is_staff ? 'var(--info-bg)' : 'var(--accent-bg)',
          color:      role.is_staff ? 'var(--info)'    : 'var(--accent-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {role.is_staff
            ? <Icon.shield size={18} />
            : <Icon.user   size={18} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {role.name}
          </div>
          <div style={{
            fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)',
            marginTop: 2,
          }}>
            {role.slug}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10, minHeight: 22 }}>
        {permissions.length === 0 ? (
          <Badge variant="neutral" size="xs">Client (portal only)</Badge>
        ) : (
          permissions.map(p => (
            <Badge key={p} variant={p === 'Staff' ? 'info' : 'accent'} size="xs">{p}</Badge>
          ))
        )}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 0 0', borderTop: '1px solid var(--border)',
        marginTop: 'auto',
      }}>
        <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
          <strong style={{ color: 'var(--text)', fontWeight: 600 }}>{memberCount}</strong> member{memberCount === 1 ? '' : 's'}
        </span>
        {canEdit && (
          <div style={{ display: 'flex', gap: 4 }}>
            <Btn variant="ghost" size="xs" icon={Icon.edit} onClick={onEdit}>Edit</Btn>
            <Btn variant="ghost" size="xs" icon={Icon.trash} onClick={onDelete} title="Delete" />
          </div>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function RoleEditModal({ mode, role, allRoles, teamId, onClose, onSaved }) {
  const isNew = mode === 'new';
  const isMobile = useIsMobile();

  const [name, setName]    = useState(role?.name || '');
  const [slug, setSlug]    = useState(role?.slug || '');
  const [slugEdited, setSlugEdited] = useState(!isNew);

  const [perms, setPerms] = useState({
    is_staff:             role?.is_staff             || false,
    can_review:           role?.can_review           || false,
    can_invite:           role?.can_invite           || false,
    can_manage_templates: role?.can_manage_templates || false,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  // Auto-derive slug from name on new roles
  useEffect(() => {
    if (isNew && !slugEdited) setSlug(toSlug(name));
  }, [name, isNew, slugEdited]);

  const togglePerm = (key) => {
    setPerms(p => {
      const next = { ...p, [key]: !p[key] };
      // Other permissions imply staff — if user enables review/invite/manage, also enable staff
      if (!next.is_staff && (next.can_review || next.can_invite || next.can_manage_templates)) {
        next.is_staff = true;
      }
      // Inverse: turning off staff also turns off all the others
      if (key === 'is_staff' && !next.is_staff) {
        next.can_review = false;
        next.can_invite = false;
        next.can_manage_templates = false;
      }
      return next;
    });
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) { setError('Role name is required.'); return; }
    const finalSlug = (slug || toSlug(name)).trim();
    if (!finalSlug) { setError('Role slug must contain at least one letter or number.'); return; }

    // Check for duplicate slug
    const dupe = allRoles.find(r => r.slug === finalSlug && r.id !== role?.id);
    if (dupe) { setError(`Another role already uses the slug "${finalSlug}".`); return; }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        slug: finalSlug,
        ...perms,
      };

      if (isNew) {
        const { error: insErr } = await supabase
          .from('team_roles')
          .insert({ team_id: teamId, ...payload });
        if (insErr) throw insErr;
      } else {
        const { error: updErr } = await supabase
          .from('team_roles')
          .update(payload)
          .eq('id', role.id);
        if (updErr) throw updErr;
      }
      onSaved();
    } catch (err) {
      console.error('Save role failed:', err);
      setError(err.message || 'Could not save role.');
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10, 15, 46, 0.55)',
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: isMobile ? 'none' : '1px solid var(--border)',
          borderRadius: isMobile ? 0 : 'var(--r-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: isMobile ? 'none' : 540,
          maxHeight: isMobile ? '100dvh' : '90vh',
          height: isMobile ? '100dvh' : 'auto',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: isMobile ? '16px 20px' : '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {isNew ? 'New role' : 'Edit role'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              Define a role and its permissions in this workspace.
            </div>
          </div>
          <Btn variant="ghost" size="sm" icon={Icon.x} onClick={onClose} title="Close" />
        </div>

        <div style={{ padding: isMobile ? '16px 20px' : '20px 24px', overflowY: 'auto', flex: 1 }}>
          {error && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}

          <FieldLabel>Display name</FieldLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Reviewer, Office Manager, Auditor"
            disabled={saving}
            style={{ marginBottom: 12 }}
          />

          <FieldLabel>Slug</FieldLabel>
          <Input
            value={slug}
            onChange={(e) => { setSlug(toSlug(e.target.value)); setSlugEdited(true); }}
            placeholder="reviewer"
            disabled={saving}
            style={{ marginBottom: 4, fontFamily: 'var(--font-mono)' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 16 }}>
            Stable identifier used in code. Lowercase letters, numbers, underscores only.
          </div>

          <FieldLabel>Permissions</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 4 }}>
            {PERMISSIONS.map(p => (
              <PermissionRow
                key={p.key}
                perm={p}
                checked={perms[p.key]}
                onToggle={() => togglePerm(p.key)}
                disabled={saving}
              />
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>
            Roles with no staff permission are <strong>clients</strong> — they can only access their upload portal.
          </div>
        </div>

        <div style={{
          padding: isMobile ? '14px 20px' : '16px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: 8,
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          flexShrink: 0,
          background: 'var(--surface)',
        }}>
          <Btn
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={saving}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >
            Cancel
          </Btn>
          <Btn
            variant="primary"
            size="md"
            iconRight={saving ? null : Icon.check}
            onClick={submit}
            disabled={saving || !name.trim()}
            style={isMobile ? { flex: 2, justifyContent: 'center' } : undefined}
          >
            {saving ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Saving…</> : (isNew ? 'Create role' : 'Save changes')}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function PermissionRow({ perm, checked, onToggle, disabled }) {
  const I = Icon[perm.icon] || Icon.shield;
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle()}
      disabled={disabled}
      style={{
        textAlign: 'left', fontFamily: 'inherit',
        padding: '12px 14px',
        background: checked ? 'var(--accent-bg)' : 'var(--surface-2)',
        border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--r-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: checked ? 'var(--accent)' : 'var(--surface)',
        color:      checked ? '#fff' : 'var(--text-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: 1,
      }}>
        <I size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 2,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{perm.label}</span>
          <span style={{
            width: 30, height: 18, borderRadius: 9, position: 'relative',
            background: checked ? 'var(--accent)' : 'var(--border-strong)',
            transition: 'background 0.15s', flexShrink: 0,
          }}>
            <span style={{
              position: 'absolute', top: 2, left: checked ? 14 : 2,
              width: 14, height: 14, borderRadius: '50%',
              background: '#fff', transition: 'left 0.15s',
            }} />
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>{perm.hint}</div>
      </div>
    </button>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      fontSize: 12, fontWeight: 600, color: 'var(--text-2)',
      display: 'block', marginBottom: 6,
    }}>
      {children}
    </label>
  );
}
